import React, { useRef, useState, DragEvent, useEffect } from 'react';
import { Button, Modal, Toast } from '@ds';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import { ACCEPTED_FILE_TYPES, validateFilePropertiesBeforeUpload } from 'src/utils/FileUtils';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { CloseIcon } from '@ds-icons';
import { uploadI94Document } from 'src/shared/services/document-api/Documents';
import { DOCUMENT_VISIBILITY } from 'src/shared/types/Documents';
import styles from './UploadI94DocumentsModal.module.scss';
import { useGetDocumentTypes } from 'src/shared/queries/useGetDocumentTypes';
import { DOCUMENT_TYPE_NAME } from 'src/utils/services/documents';

interface UploadI94DocumentsModalProps {
  visible: boolean;
  onClose: () => void;
  onUploadComplete?: () => Promise<void> | void;
}

const UploadI94DocumentsModal: React.FC<UploadI94DocumentsModalProps> = ({ visible, onClose, onUploadComplete }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toastRef = useRef<IToastr>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const { data: userInfo } = useGetUser();
  const { data: dataDocumentTypes } = useGetDocumentTypes();
  const i94DocumentType = useRef<number | null>(null);

  useEffect(() => {
    if (dataDocumentTypes && dataDocumentTypes.length > 0) {
      const i94Doc = dataDocumentTypes.find(
        (type) => type.docTypeName === DOCUMENT_TYPE_NAME.GOVERNMENT_IMMIGRATION_DOCUMENT
      );
      if (i94Doc) {
        i94DocumentType.current = i94Doc.docTypeId;
      }
    }
  }, [dataDocumentTypes]);

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const combinedFiles = [...selectedFiles, ...fileArray];
    const validationResult = validateFilePropertiesBeforeUpload(combinedFiles);

    if (!validationResult.isValid) {
      toastRef.current?.show({
        message: validationResult.errors[0],
        severity: 'error',
      });
      return;
    }

    setSelectedFiles(combinedFiles);
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !userInfo) return;

    if (i94DocumentType.current === null) {
      toastRef.current?.show({
        message: 'Unable to upload I-94 document type. Please try again later.',
        severity: 'error',
      });
      return;
    }

    setIsUploading(true);

    const beneId = Number(userInfo.beneId);
    const numFiles = selectedFiles.length;
    let numUploaded = 0;
    let numFailed = 0;

    for (const file of selectedFiles) {
      toastRef.current?.show({
        message: 'Uploading...',
        rightMessage: `${numFiles - numUploaded - numFailed} ${numFiles - numUploaded - numFailed > 1 ? 'Files' : 'File'} left`,
        severity: 'info',
        sticky: true,
      });

      const metaData = {
        name: file.name,
        type: i94DocumentType.current,
        passport: null,
        immigration: {},
        common: {
          beneId,
          clientCaseIds: [],
          companyId: userInfo.companyId,
          visibility: DOCUMENT_VISIBILITY,
        },
      };

      try {
        await uploadI94Document([file], metaData, userInfo.userId, beneId);
        numUploaded++;
      } catch (error) {
        numFailed++;
        toastRef.current?.clear();
        toastRef.current?.show({
          message: `Failed to upload "${file.name}"`,
          severity: 'error',
        });
      } finally {
        toastRef.current?.clear();
      }
    }

    toastRef.current?.clear();
    if (numFailed > 0 && numUploaded === 0) {
      toastRef.current?.show({
        message: `Failed to upload ${numFailed} ${numFailed > 1 ? 'files' : 'file'}`,
        severity: 'error',
      });
    } else if (numFailed > 0) {
      toastRef.current?.show({
        message: `Uploaded ${numUploaded} ${numUploaded > 1 ? 'files' : 'file'}, ${numFailed} failed`,
        severity: 'warn',
      });
    } else {
      toastRef.current?.show({
        message: 'Uploaded',
        rightMessage: `${numUploaded} ${numUploaded > 1 ? 'Files' : 'File'}`,
        severity: 'success',
        sticky: false,
      });
    }

    setSelectedFiles([]);
    setIsUploading(false);
    if (numUploaded > 0) {
      await onUploadComplete?.();
    }
    onClose();
  };

  const onFileInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    e.target.value = '';
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const onFileDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const handleClose = () => {
    setSelectedFiles([]);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      onModalClosed={handleClose}
      className={`w-full md:!w-2/3 lg:!w-[772px] ${styles['upload-i94-modal']}`}
      modalClassName={'!p-[24px]'}
      title="I-94 UPLOAD"
    >
      <input type="file" ref={fileInputRef} onChange={onFileInputChanged} style={{ display: 'none' }} multiple />
      <div className={styles['upload-i94-modal-content']} onDragOver={onDragOver} onDrop={onFileDrop}>
        <div className={styles['upload-icon']}></div>
        <div className="text-center">
          <p className={styles['upload-docs-description']}>Drag and drop or select files from your device</p>
          <span className={styles['supported-formats']}>We support {ACCEPTED_FILE_TYPES.join(', ')}.</span>
        </div>
        <Button className={styles['select-button']} onClick={openFileDialog} disabled={isUploading}>
          SELECT
        </Button>
      </div>

      {selectedFiles.length > 0 && (
        <div className={styles['selected-files-section']}>
          <ul className={styles['selected-files-list']}>
            {selectedFiles.map((file, index) => (
              <li key={index} className={styles['selected-file-item']}>
                <div className={styles['file-info']}>
                  <div className={styles['file-upload-icon']}></div>
                  <span className={styles['file-name']}>{file.name}</span>
                </div>
                <img
                  alt="close-icon"
                  className={styles['close-icon']}
                  src={CloseIcon}
                  onClick={() => removeFile(index)}
                />
              </li>
            ))}
          </ul>
          <div className={styles['upload-actions']}>
            <Button onClick={handleClose} disabled={isUploading} variant="outlined" styleType="secondary">
              CANCEL
            </Button>
            <Button onClick={handleUpload} disabled={isUploading}>
              {isUploading ? 'UPLOADING...' : 'UPLOAD'}
            </Button>
          </div>
        </div>
      )}

      <Toast ref={toastRef} />
    </Modal>
  );
};

export default UploadI94DocumentsModal;
